import { useState, useEffect, useMemo } from "react";
import { IoIosClose } from "react-icons/io";
import { FiSend } from "react-icons/fi";
import TextareaAutosize from "react-textarea-autosize";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/lib/AnalyticsContext";
import { usePlayer } from "@/lib/PlayerContext";

export interface ChatInputModalProps {
  open: boolean;
  onClose: (status: boolean) => void;
  onSave: (nickname: string) => void;
  message?: string;
  onMessageChange?: (message: string) => void;
  onSendMessage?: () => void;
  hasNickname?: boolean;
  fullScreen?: boolean;
  playerWidth?: number;
  playerId?: string;
  currentNickname?: string;
  isSending?: boolean;
}

const ChatInputModal = ({
  open = false,
  onClose,
  onSave,
  message = "",
  onMessageChange,
  onSendMessage,
  hasNickname = false,
  fullScreen = false,
  playerWidth = 270,
  currentNickname = "",
  isSending = false,
}: ChatInputModalProps) => {
  const [nickname, setNickname] = useState<string>(currentNickname);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasSetNickname, setHasSetNickname] = useState(hasNickname);
  const [messageError, setMessageError] = useState<string>("");
  const [nicknameError, setNicknameError] = useState<string>("");
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { meta } = usePlayer();

  useEffect(() => {
    setHasSetNickname(hasNickname);
  }, [hasNickname]);

  useEffect(() => {
    setNickname(currentNickname);
  }, [currentNickname]);

  function closeModal() {
    setIsAnimating(false);
    setTimeout(() => {
      onClose(false);
    }, 300);
  }

  const disabled = useMemo(
    () => nickname === "" || nicknameError !== "",
    [nickname, nicknameError]
  );

  const handleNicknameChange = (value: string) => {
    setNickname(value);
    if (value.length > 20) {
      setNicknameError("ニックネームは20文字以内で入力してください");
    } else {
      setNicknameError("");
    }
  };

  const handleNicknameSave = () => {
    if (nickname.length <= 20 && nickname !== "") {
      onSave(nickname);
      setHasSetNickname(true);
    }
  };

  const messageDisabled = useMemo(() => {
    return !message.trim() || message.length > 200 || isSending;
  }, [message, isSending]);

  const handleMessageChange = (value: string) => {
    if (value.length > 200) {
      setMessageError(t("chat.maxCharactersError"));
    } else {
      setMessageError("");
    }
    onMessageChange?.(value);
  };

  const handleSendMessage = () => {
    if (message.length > 200) {
      setMessageError(t("chat.maxCharactersError"));
      return;
    }
    if (onSendMessage) {
      onSendMessage();
      analytics.sendLiveCommerceEvent.chat("viewer", meta.id);
    }
  };

  useEffect(() => {
    if (open) {
      setIsAnimating(true);
    } else {
      setIsAnimating(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={`tw-absolute tw-inset-0 tw-z-50 tw-flex tw-items-end ${
        fullScreen ? "tw-justify-center" : ""
      }`}
      style={{
        backgroundColor: fullScreen ? "rgba(0, 0, 0, 0.5)" : "transparent",
        position: "absolute",
        width: fullScreen ? "100%" : `${playerWidth}px`,
        height: "100%",
        left: 0,
        top: 0,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div
        className={`tw-bg-white/70 tw-rounded-t-[20px] tw-transition-transform tw-duration-300 tw-ease-in-out tw-pointer-events-auto`}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          margin: "0 8px",
          width: `calc(100% - 16px)`,
          height: "250px",
          transform: isAnimating ? "translateY(0)" : "translateY(100%)",
        }}
      >
        <div className="tw-relative tw-h-full tw-py-2">
          <div className="tw-font-sans tw-h-full tw-overflow-auto">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-1 tw-border-b tw-border-gray-200">
              <div className="tw-font-medium tw-text-gray-800">
                {t("chat.title")}
              </div>
              <button
                className="tw-text-gray-800 tw-bg-transparent tw-border-none tw-outline-none tw-shadow-none hover:tw-cursor-pointer "
                onClick={closeModal}
              >
                <IoIosClose size="1.5em" />
              </button>
            </div>
            {!hasSetNickname ? (
              <div className="tw-p-5">
                <div className="tw-text-center tw-font-sans">
                  <input
                    className={`tw-shadow tw-appearance-none tw-border tw-rounded tw-w-[95%] tw-py-2 tw-px-3 tw-text-gray-700 tw-leading-tight focus:tw-outline-none focus:tw-shadow-outline tw-mt-4 placeholder:tw-text-xs tw-text-center ${
                      nicknameError ? "tw-border-red-500" : ""
                    }`}
                    id="nickname"
                    type="text"
                    placeholder={t("chat.enterNickname")}
                    value={nickname}
                    onChange={(event) =>
                      handleNicknameChange(event.target.value)
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !disabled) {
                        handleNicknameSave();
                      }
                    }}
                  />
                  {nicknameError && (
                    <div className="tw-text-red-500 tw-text-xs tw-mt-2">
                      {nicknameError}
                    </div>
                  )}
                </div>
                <div className="tw-text-center">
                  <button
                    disabled={disabled}
                    className={`${
                      disabled
                        ? "tw-bg-gray-400"
                        : "tw-bg-green-600 hover:tw-bg-green-500"
                    } tw-rounded-full tw-m-auto tw-px-6 tw-py-1 tw-my-4 tw-border-none tw-outline-none tw-shadow-none tw-text-white`}
                    onClick={handleNicknameSave}
                  >
                    {t("common.ok")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="tw-h-full tw-flex tw-flex-col">
                <div className="tw-px-4 tw-py-2">
                  <div className="tw-text-sm tw-text-gray-600">
                    <span className="tw-ml-1 tw-font-medium tw-text-gray-800">
                      {nickname || currentNickname}
                    </span>
                  </div>
                </div>
                <div className="tw-absolute tw-bottom-5 tw-left-5 tw-right-5">
                  <TextareaAutosize
                    className="tw-box-border tw-w-full tw-rounded-[7px] tw-border tw-border-gray-300 tw-py-2 tw-px-3 tw-text-gray-700 tw-text-sm focus:tw-outline-none focus:tw-border-green-500"
                    placeholder={t("chat.messagePlaceholder")}
                    value={message}
                    minRows={3}
                    maxRows={3}
                    onChange={(event) =>
                      handleMessageChange(event.target.value)
                    }
                  />
                  {messageError && (
                    <div className="tw-text-red-500 tw-text-xs tw-mt-1 tw-px-1">
                      {messageError}
                    </div>
                  )}
                  <div className="tw-flex tw-justify-between tw-items-center tw-mt-1">
                    <div
                      className={`tw-text-xs tw-px-1 ${message.length > 200 ? "tw-text-red-500" : "tw-text-gray-500"}`}
                    >
                      {message.length}/200
                    </div>
                  </div>
                  <button
                    className={`tw-flex tw-items-center tw-justify-center tw-w-full tw-mt-3 tw-rounded-[7px] tw-py-2 tw-px-4 tw-border-none tw-outline-none tw-text-white ${
                      messageDisabled
                        ? "tw-bg-gray-400 tw-cursor-not-allowed"
                        : "tw-bg-green-600 hover:tw-bg-green-500 tw-cursor-pointer"
                    }`}
                    onClick={handleSendMessage}
                    disabled={messageDisabled}
                  >
                    {isSending ? (
                      <>
                        <svg
                          className="tw-animate-spin tw-mr-2"
                          width="1.2rem"
                          height="1.2rem"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="tw-opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="tw-opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>{t("player.comment.sending")}</span>
                      </>
                    ) : (
                      <>
                        <FiSend size="1.2rem" className="tw-mr-2" />
                        <span>{t("player.comment.send")}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInputModal;
